import { apiClient } from '../../lib/apiClient';
import type {
  AssetCreate,
  AssetRead,
  AssetUpdate,
  BrandIdentityRead,
  BrandIdentityUpdate,
  BrandVoiceExampleCreate,
  BrandVoiceExampleRead,
  BrandVoiceExampleUpdate,
  CalendarEventCreate,
  CalendarEventRead,
  CalendarEventUpdate,
  CopyCheckCreate,
  CopyCheckRead,
  InspirationCreate,
  InspirationRead,
  KbReadiness,
  ProductCreate,
  ProductRead,
  ProductUpdate,
  ProofPointCreate,
  ProofPointRead,
  ProofPointUpdate,
} from '../../types/api';

/** Every knowledge-base route is scoped to a business (SRS §4 ownership root). */
function businessPath(businessId: string, segment: string): string {
  return `/api/v1/businesses/${businessId}/${segment}`;
}

/* ---------------------------------------------------------------- */
/* Brand identity — singleton                                        */
/* ---------------------------------------------------------------- */

export function fetchBrandIdentity(
  businessId: string,
  signal?: AbortSignal,
): Promise<BrandIdentityRead> {
  return apiClient.get<BrandIdentityRead>(businessPath(businessId, 'brand-identity'), { signal });
}

export function updateBrandIdentity(
  businessId: string,
  body: BrandIdentityUpdate,
): Promise<BrandIdentityRead> {
  return apiClient.put<BrandIdentityRead>(businessPath(businessId, 'brand-identity'), { body });
}

/* ---------------------------------------------------------------- */
/* Readiness + copy check — derived reads over the same knowledge base */
/* ---------------------------------------------------------------- */

/** How much of the knowledge base the pipeline can actually ground on. */
export function fetchKbReadiness(businessId: string, signal?: AbortSignal): Promise<KbReadiness> {
  return apiClient.get<KbReadiness>(businessPath(businessId, 'kb-readiness'), { signal });
}

/**
 * Runs draft copy past the deterministic copy guardrails (never-say list,
 * platform character limits, PII patterns). No LLM call, no quota spent.
 */
export function checkCopy(
  businessId: string,
  body: CopyCheckCreate,
  signal?: AbortSignal,
): Promise<CopyCheckRead> {
  return apiClient.post<CopyCheckRead>(businessPath(businessId, 'copy-check'), { body, signal });
}

/* ---------------------------------------------------------------- */
/* Collections                                                       */
/* ---------------------------------------------------------------- */

/**
 * The five collection endpoints share one REST shape:
 * `GET /` (newest first), `POST /` (201), `PUT /{id}`, `DELETE /{id}` (204).
 *
 * A newer collection may answer a partial `PATCH /{id}` instead of the
 * whole-record PUT; that is the one axis {@link CollectionApiOptions} moves.
 */
export interface CollectionApi<TRead, TCreate, TUpdate> {
  list: (businessId: string, signal?: AbortSignal) => Promise<TRead[]>;
  create: (businessId: string, body: TCreate) => Promise<TRead>;
  update: (businessId: string, id: string, body: TUpdate) => Promise<TRead>;
  remove: (businessId: string, id: string) => Promise<void>;
}

export interface CollectionApiOptions {
  /**
   * Verb behind `update`. The knowledge base replaces a whole record, so PUT is
   * the default; a collection whose update body is a partial (the newsfeed's
   * categories, and the one field of a feed source that can move) says PATCH
   * here rather than growing a second factory.
   */
  updateMethod?: 'PUT' | 'PATCH';
}

/**
 * Builds a client for one of those endpoints.
 *
 * Exported because the shape is not knowledge-base specific — the content
 * calendar's `campaigns` and `planned-posts` collections are the same REST
 * shape under the same business root, and reimplementing it there would be a
 * second place for the undo-window `keepalive` rule to drift.
 */
export function createCollectionApi<TRead, TCreate, TUpdate>(
  segment: string,
  { updateMethod = 'PUT' }: CollectionApiOptions = {},
): CollectionApi<TRead, TCreate, TUpdate> {
  return {
    list: (businessId, signal) =>
      apiClient.get<TRead[]>(businessPath(businessId, segment), { signal }),
    create: (businessId, body) =>
      apiClient.post<TRead>(businessPath(businessId, segment), { body }),
    update: (businessId, id, body) => {
      const path = `${businessPath(businessId, segment)}/${id}`;
      return updateMethod === 'PATCH'
        ? apiClient.patch<TRead>(path, { body })
        : apiClient.put<TRead>(path, { body });
    },
    // Deletes are deferred behind an undo window and may be flushed while the
    // page is unloading, so the request is allowed to outlive it.
    remove: (businessId, id) =>
      apiClient.delete<void>(`${businessPath(businessId, segment)}/${id}`, { keepalive: true }),
  };
}

export const brandVoiceExamplesApi = createCollectionApi<
  BrandVoiceExampleRead,
  BrandVoiceExampleCreate,
  BrandVoiceExampleUpdate
>('brand-voice-examples');

export const productsApi = createCollectionApi<ProductRead, ProductCreate, ProductUpdate>(
  'products',
);

export const proofPointsApi = createCollectionApi<
  ProofPointRead,
  ProofPointCreate,
  ProofPointUpdate
>('proof-points');

export const calendarEventsApi = createCollectionApi<
  CalendarEventRead,
  CalendarEventCreate,
  CalendarEventUpdate
>('calendar-events');

export const assetsApi = createCollectionApi<AssetRead, AssetCreate, AssetUpdate>('assets');

/** No PUT in v1: an inspiration is added or removed, never edited. */
export const inspirationsApi = createCollectionApi<InspirationRead, InspirationCreate, never>(
  'inspirations',
);
